import { NextPageContext } from 'next';
import { useRouter } from 'next/router';
import { useContext } from 'react';

import { MetaHead, getMetadataFromSearchParams } from 'common/metaHead';
import { getStayLength, getTripInfo, parseRoutes } from 'common/utils';
import { initApolloClient } from 'config/apollo';
import { DeviceContext, DeviceType } from 'contexts/device';
import { InternationalContextProvider } from 'contexts/international';
import { InternationalFilterContextProvider } from 'contexts/internationalFilter';
import { PriceTransparencyProvider } from 'contexts/priceTransparency';
import { ReloadTimerContextProvider } from 'contexts/reloadTimer';
import { SearchParamsContextProvider } from 'contexts/searchParams';
import {
  IAirportDetail,
  getAirportList,
  getSummarizedLocationCodes,
} from 'hooks/useAirportDetailList';
import { defaultVariables } from 'hooks/useInternationalFlight';
import useNxAirlineParam from 'hooks/useNxAirlineParam';
import InternationalContainer from 'list/international';
import config from 'config/index';
import Script from 'next/script';

interface IProps {
  description: string;
  image?: string;
  itineraryIataDetails: IAirportDetail[];
}

const International = ({
  description,
  image,
  itineraryIataDetails,
}: IProps) => {
  const device = useContext(DeviceContext);
  const router = useRouter();

  // 통검에서 목록으로 직접 넘어오는 항공사 질의처리
  useNxAirlineParam({ router });

  const {
    adult: radult,
    child: rchild,
    infant: rinfant,
    fareType,
    where,
    isDirect,
    samePriceOrgSch,
    selectedFlight,
    selectType,
    routes = [],
  }: {
    adult?: string;
    child?: string;
    infant?: string;
    fareType?: string;
    where?: string;
    isDirect?: string;
    samePriceOrgSch?: string;
    selectedFlight?: string;
    selectType?: string;
    routes?: string[];
  } = router.query || {};
  const [adult, child, infant] = [
    +(radult || defaultVariables.adult),
    +(rchild || defaultVariables.child),
    +(rinfant || defaultVariables.infant),
  ];

  const itinerary = parseRoutes(routes);
  const stayLength = getStayLength(itinerary);
  const queryVariables = {
    ...defaultVariables,
    trip: getTripInfo(itinerary),
    adult,
    child,
    infant,
    itinerary,
    fareType: fareType || defaultVariables.fareType,
    where: where || (device === DeviceType.MOBILE ? 'mobile' : 'pc'),
    isDirect: isDirect === 'true',
    stayLength: stayLength || defaultVariables.stayLength,
    selectedFlight,
    selectType,
  };

  return (
    <>
      <script
        src={`${config.cdnDomain}/flight/vertical-static/libs/jquery/jquery-2.2.4.min.js`}
      />
      <script
        src={`${config.cdnDomain}/flight/vertical-static/libs/egjs/eg.pkgd-1.4.1.min.js`}
      />
      <script
        src={`${config.cdnDomain}/flight/vertical-static/libs/inline-viewer/inline-viewer-1.1.2.min.js`}
      />
      <MetaHead description={description} image={image} />
      <InternationalContextProvider queryVariables={queryVariables}>
        <InternationalFilterContextProvider
          queryVariables={queryVariables}
          itineraryIataDetails={itineraryIataDetails}
        >
          <SearchParamsContextProvider>
            <ReloadTimerContextProvider router={router}>
              <PriceTransparencyProvider>
                <InternationalContainer
                  queryVariables={queryVariables}
                  samePriceOrgSch={samePriceOrgSch}
                  itineraryIataDetails={itineraryIataDetails}
                />
              </PriceTransparencyProvider>
            </ReloadTimerContextProvider>
          </SearchParamsContextProvider>
        </InternationalFilterContextProvider>
      </InternationalContextProvider>
    </>
  );
};

International.getInitialProps = async ({ query }: NextPageContext) => {
  const { description, image } = await getMetadataFromSearchParams(query);
  const client = initApolloClient();
  const itineraries = parseRoutes(query.routes as string[]);
  const locationCodes = getSummarizedLocationCodes(itineraries);
  const itineraryIataDetails = await getAirportList(client, locationCodes);
  return { description, image, itineraryIataDetails };
};

export default International;
